import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import api, {
    AiFeesLeaderboardEntry,
    HostsLeaderboardEntry,
    MintersLeaderboardEntry,
    ShareRewardsLeaderboardEntry,
    UpvalueLeaderboardCategory,
} from 'api/api'
import LeaderboardPanel, { LEADERBOARD_CATEGORIES, LeaderboardPanelItem } from './LeaderboardPanel'
import styles from './upvalue-leaderboard-widget.module.scss'
import useStore from 'state/knovStore'
import { subscribeLeaderboardTicks } from 'state/leaderboardTicks'
import { openCenter, withDepth } from 'components/filters/useStreamFilters'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import DailyMoneyValue from 'components/shared/DailyMoneyValue'
import useDailyMoney from 'components/shared/useDailyMoney'

interface UpvalueLeaderboardWidgetProps {
    className?: string
    onClose?: () => void
    onContentLayoutSettled?: () => void
    onShare?: () => void
    onJoin?: () => void
    range?: 'today' | '1d'
    date?: string
    embedded?: boolean
}

const toUtcYmd = (d: Date) => {
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

const addUtcDays = (ymd: string, days: number) => {
    const [year, month, day] = ymd.split('-').map(Number)
    const date = new Date(Date.UTC(year, month - 1, day))
    date.setUTCDate(date.getUTCDate() + days)
    return toUtcYmd(date)
}

const formatUtcDateLabel = (ymd: string) => {
    const [year, month, day] = ymd.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    })
}

export default function UpvalueLeaderboardWidget({
    className,
    onClose,
    onContentLayoutSettled,
    onJoin,
    onShare,
    range = 'today',
    date,
    embedded = false,
}: UpvalueLeaderboardWidgetProps) {
    const openLeaderboardModal = useStore(state => state.actions.modalActions.openLeaderboardModal)
    const [nowMs, setNowMs] = useState(() => Date.now())
    const { formatSatsAmount } = useBsvDisplay()
    // The shareable embed follows the daily money rule (what you see is what
    // the share card says); the sidebar widget keeps the viewer's unit.
    const dailyMoney = useDailyMoney({ enabled: embedded })
    const queryClient = useQueryClient()
    const isLive = range === 'today' && !date

    const onSelectUser = (userId: string) => {
        openCenter(withDepth({ user: userId }))
        if (onClose) onClose()
    }

    const startOfTodayUtc = useMemo(() => {
        const d = new Date(nowMs)
        d.setUTCHours(0, 0, 0, 0)
        return toUtcYmd(d)
    }, [nowMs])

    const rangeParams = useMemo(() => {
        if (date) {
            return {
                start_date: date,
                end_date: addUtcDays(date, 1),
                queryKeyPart: ['date', date],
            }
        }

        if (range === '1d') {
            const startDate = addUtcDays(startOfTodayUtc, -1)
            return {
                start_date: startDate,
                end_date: startOfTodayUtc,
                queryKeyPart: ['range', range, startDate, startOfTodayUtc],
            }
        }

        return {
            start_date: startOfTodayUtc,
            end_date: undefined,
            queryKeyPart: ['range', range, startOfTodayUtc],
        }
    }, [date, range, startOfTodayUtc])

    useEffect(() => {
        if (range !== 'today' || !!date) return
        const id = window.setInterval(() => setNowMs(Date.now()), 60_000)
        return () => window.clearInterval(id)
    }, [date, range])

    // Live views refetch when the LeaderboardChannel broadcaster signals a board
    // moved (e.g. a share-reward settlement landed). Historical windows are
    // cache-stable, so they skip the subscription.
    useEffect(() => {
        if (!isLive) return
        return subscribeLeaderboardTicks(() => {
            queryClient.invalidateQueries({ queryKey: ['upvalueLeaderboard', 'widget'] })
        })
    }, [isLive, queryClient])

    const elapsedSinceUtcMidnightLabel = useMemo(() => {
        const start = new Date()
        start.setUTCHours(0, 0, 0, 0)
        const elapsedMs = Math.max(0, nowMs - start.getTime())
        const totalMinutes = Math.floor(elapsedMs / 60_000)
        if (totalMinutes < 60) return `${totalMinutes}m`
        const hoursRounded = Math.round(totalMinutes / 60)
        return `${hoursRounded}h`
    }, [nowMs])

    const queryResults = useQueries({
        queries: LEADERBOARD_CATEGORIES.map(c => ({
            queryKey: ['upvalueLeaderboard', 'widget', c.key, ...rangeParams.queryKeyPart, 1],
            queryFn: async () => {
                if (c.key === 'ai_fees') {
                    return await api.getAiFeesLeaderboard({
                        page: 1,
                        per_page: 1,
                        start_date: rangeParams.start_date,
                        end_date: rangeParams.end_date,
                    })
                }
                if (c.key === 'minters') {
                    return await api.getMintersLeaderboard({
                        page: 1,
                        per_page: 1,
                        start_date: rangeParams.start_date,
                        end_date: rangeParams.end_date,
                    })
                }
                if (c.key === 'hosts') {
                    return await api.getHostsLeaderboard({
                        page: 1,
                        per_page: 1,
                        start_date: rangeParams.start_date,
                        end_date: rangeParams.end_date,
                    })
                }
                if (c.key === 'sharers') {
                    return await api.getShareRewardsLeaderboard({
                        page: 1,
                        per_page: 1,
                        start_date: rangeParams.start_date,
                        end_date: rangeParams.end_date,
                    })
                }
                return await api.getUpvalueLeaderboard({
                    category: c.key as UpvalueLeaderboardCategory,
                    page: 1,
                    per_page: 1,
                    start_date: rangeParams.start_date,
                    end_date: rangeParams.end_date,
                })
            },
            staleTime: range === 'today' && !date ? 60_000 : 5 * 60_000,
            refetchInterval: range === 'today' && !date ? 60_000 : false,
        })),
    })

    const isLoading = queryResults.some(r => r.isLoading)
    const error = queryResults.some(r => r.error)

    const formatCount = useMemo(() => (value: number) => (value || 0).toLocaleString(), [])

    const items = useMemo<LeaderboardPanelItem[]>(
        () =>
            LEADERBOARD_CATEGORIES.flatMap((category, idx) => {
                const data = queryResults[idx].data as any
                const item = data?.users?.[0]
                if (!item) return []

                const formatMoney = (sats: number) =>
                    embedded ? (
                        <DailyMoneyValue money={dailyMoney(sats)} className={styles.moneyValue} />
                    ) : (
                        formatSatsAmount(sats, { compactSats: false })
                    )
                const valueLabel =
                    category.key === 'ai_fees'
                        ? formatMoney((item as AiFeesLeaderboardEntry).total_sats)
                        : category.key === 'minters'
                        ? `${formatCount((item as MintersLeaderboardEntry).mints_count)} ${
                              (item as MintersLeaderboardEntry).mints_count === 1 ? 'mint' : 'mints'
                          }`
                        : category.key === 'hosts'
                        ? formatMoney((item as HostsLeaderboardEntry).total_sats)
                        : category.key === 'sharers'
                        ? formatMoney((item as ShareRewardsLeaderboardEntry).total_sats)
                        : formatMoney(item.total_sats)

                return [
                    {
                        category,
                        user: item.user,
                        valueLabel,
                    },
                ]
            }),
        [dailyMoney, embedded, formatCount, formatSatsAmount, queryResults],
    )

    const headerLabel = useMemo<ReactNode>(() => {
        if (embedded && date) {
            return (
                <span className={styles.embeddedHeaderLabel}>
                    <span className={styles.embeddedHeaderTitle}>TREECHAT LEADERS</span>
                    <span className={styles.embeddedHeaderDate}>{formatUtcDateLabel(date)}</span>
                </span>
            )
        }
        if (date) return `${formatUtcDateLabel(date)} Leaders`
        if (range === '1d') return "Yesterday's Leaders"
        return (
            <>
                Today's Top{' '}
                <span className={styles.utcElapsed}>{elapsedSinceUtcMidnightLabel}</span>
            </>
        )
    }, [date, elapsedSinceUtcMidnightLabel, embedded, range])

    useEffect(() => {
        if (isLoading) return
        onContentLayoutSettled?.()
    }, [error, isLoading, items.length, onContentLayoutSettled])

    return (
        <LeaderboardPanel
            className={className}
            embedded={embedded}
            error={!!error}
            headerLabel={headerLabel}
            isLoading={isLoading}
            items={items}
            onOpenLeaderboardModal={() => openLeaderboardModal('received')}
            onSelectUser={onSelectUser}
            onShare={onShare}
            onJoin={onJoin}
        />
    )
}
